import React from "react";

/**
 * Lightweight, instant-render molecule poster art.
 *
 * Used on product CARDS (Shop grid, Home grid, Featured rails) where the previous
 * 3D viewer + IntersectionObserver + PubChem fetch added ~1-3 seconds before the
 * card "looked complete" on slower devices. The detail page still uses the full
 * interactive 3Dmol viewer where users actually rotate/zoom.
 *
 * Renders a pure inline SVG — zero JS, zero network, paints in the same frame
 * as the rest of the card text. Stable per-molecule seed so each product has its
 * own deterministic visual signature.
 */

// Fast deterministic hash → small integer (so the same molecule always renders identically)
function hash(str) {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
    return h;
}

const PALETTES = [
    { core: "#d4af37", ring: "#10b981", glow: "#10b981" }, // gold + emerald (default brand)
    { core: "#10b981", ring: "#d4af37", glow: "#d4af37" }, // emerald core + gold ring
    { core: "#5b9cff", ring: "#10b981", glow: "#5b9cff" }, // blue + emerald
    { core: "#d4af37", ring: "#5b9cff", glow: "#d4af37" }, // gold + blue
];

export const MoleculePoster = ({ molecule, className = "", label }) => {
    const seedStr = molecule?.id ? `${molecule.source || "p"}:${molecule.id}` : (label || "default");
    const seed = hash(seedStr);

    // Determine if dual blend — render two posters side-by-side
    if (molecule?.dual) {
        return (
            <div className={`relative bg-ink-elevated ${className}`} data-testid="molecule-poster-dual">
                <div className="grid grid-cols-2 h-full w-full gap-[1px] bg-ink-strong">
                    <SinglePoster molecule={molecule.left} seedOffset={0} />
                    <SinglePoster molecule={molecule.right} seedOffset={7} />
                </div>
            </div>
        );
    }

    return (
        <div className={`relative overflow-hidden bg-ink-elevated ${className}`} data-testid="molecule-poster">
            <SinglePoster molecule={molecule} seedOffset={0} fullSeed={seed} />
        </div>
    );
};

const SinglePoster = ({ molecule, seedOffset = 0, fullSeed }) => {
    const seedStr = molecule?.id ? `${molecule.source || "p"}:${molecule.id}` : "x";
    const seed = (fullSeed ?? hash(seedStr)) + seedOffset;
    const palette = PALETTES[seed % PALETTES.length];
    const isProtein = molecule?.source === "pdb";

    // Six-atom hexagonal molecule "halo" — peptide vibe, instant render
    const atoms = [];
    const nodeCount = isProtein ? 8 : 6;
    for (let i = 0; i < nodeCount; i++) {
        const angle = (i * (360 / nodeCount) + (seed % 30)) * (Math.PI / 180);
        const radius = 62 + ((seed >> (i + 1)) & 0x7) * 1.5;
        atoms.push({
            x: 110 + Math.cos(angle) * radius,
            y: 110 + Math.sin(angle) * radius,
            r: isProtein ? 7 : 9,
        });
    }

    // Secondary smaller satellites (for visual richness)
    const satellites = [];
    for (let i = 0; i < 4; i++) {
        const angle = ((i * 90) + 45 + (seed % 25)) * (Math.PI / 180);
        const radius = 92 + ((seed >> (i + 2)) & 0x3) * 2;
        satellites.push({
            x: 110 + Math.cos(angle) * radius,
            y: 110 + Math.sin(angle) * radius,
        });
    }

    const gradId = `mp-grad-${seed}`;

    return (
        <div className="absolute inset-0 w-full h-full">
            <svg
                viewBox="0 0 220 220"
                className="absolute inset-0 w-full h-full"
                preserveAspectRatio="xMidYMid meet"
                aria-hidden="true"
            >
                <defs>
                    <radialGradient id={gradId} cx="50%" cy="50%">
                        <stop offset="0%" stopColor={palette.glow} stopOpacity="0.32" />
                        <stop offset="55%" stopColor={palette.glow} stopOpacity="0.08" />
                        <stop offset="100%" stopColor={palette.glow} stopOpacity="0" />
                    </radialGradient>
                </defs>

                {/* Outer glow */}
                <circle cx="110" cy="110" r="105" fill={`url(#${gradId})`} />

                {/* Inner faint ring */}
                <circle
                    cx="110"
                    cy="110"
                    r="78"
                    fill="none"
                    stroke={palette.ring}
                    strokeOpacity="0.18"
                    strokeWidth="0.6"
                    strokeDasharray="3 4"
                />

                {/* Spokes from atoms to core */}
                {atoms.map((a, i) => (
                    <line
                        key={`l-${i}`}
                        x1="110"
                        y1="110"
                        x2={a.x}
                        y2={a.y}
                        stroke={palette.ring}
                        strokeOpacity="0.45"
                        strokeWidth="1.1"
                    />
                ))}

                {/* Atoms */}
                {atoms.map((a, i) => (
                    <g key={`a-${i}`}>
                        <circle
                            cx={a.x}
                            cy={a.y}
                            r={a.r + 3}
                            fill={palette.ring}
                            fillOpacity="0.12"
                        />
                        <circle
                            cx={a.x}
                            cy={a.y}
                            r={a.r}
                            fill="#0f1620"
                            stroke={palette.ring}
                            strokeWidth="1.4"
                        />
                    </g>
                ))}

                {/* Satellites */}
                {satellites.map((s, i) => (
                    <circle
                        key={`s-${i}`}
                        cx={s.x}
                        cy={s.y}
                        r="2.5"
                        fill={palette.ring}
                        fillOpacity="0.55"
                    />
                ))}

                {/* Core nucleus */}
                <circle cx="110" cy="110" r="16" fill="#0f1620" stroke={palette.core} strokeWidth="1.5" />
                <circle cx="110" cy="110" r="9" fill={palette.core} />
                <circle cx="110" cy="110" r="3.5" fill="#0f1620" />
            </svg>
        </div>
    );
};
